/**
 * Client-side tenant resolution — mirrors the backend Tenant Resolver
 * (domain / subdomain / JWT) [STD-ARCH] for the pieces the SPA needs:
 *   acme.zeredu.com  -> slug "acme"   (subdomain)
 *   okul-ozel.com    -> custom domain (backend maps it; slug unknown here)
 *   localhost:3001   -> dev fallback  (VITE_DEV_TENANT_ID via the app)
 */

export type TenantSource = 'subdomain' | 'custom-domain' | 'dev-fallback' | 'configured' | 'none';

export interface TenantResolution {
  /** tenant slug when it can be derived client-side; null for custom domains */
  slug: string | null;
  /** full hostname — for custom domains the backend resolves tenant from it */
  hostname: string;
  source: TenantSource;
}

const LOCAL_HOSTS = new Set(['localhost', '127.0.0.1', '0.0.0.0', '[::1]']);
/**
 * Kiracı OLMAYAN alt alan adları.
 *
 * ⚠ UYGULAMA ETİKETLERİ DE BURADA OLMAK ZORUNDA (auth-ui appRegistry `prodSubdomain` ile birebir).
 * Tek kiracılı canlı topolojide her SPA `<uygulama>.zercode.uk` üzerinde yaşar ve hostname'de kiracı
 * etiketi YOKTUR. 'admin' listede olduğu için admin paneli null'a düşüyordu (görünen arıza), ama
 * 'student'/'teacher' listede OLMADIĞI için o SPA'lar sessizce slug='student' / slug='teacher'
 * çözüyordu — VAR OLMAYAN kiracılar. Yani yalnızca ayar ekranı boş kalmıyordu; öğrenci ve öğretmen
 * portalları her isteği yanlış kiracıyla yapıyordu (2026-07-31).
 *
 * Çok kiracılı topolojiyi ETKİLEMEZ: `student.okul.zercode.uk` için etiket zaten tabana en yakın
 * olandan (`okul`) alınır, uygulama etiketi atılır. Bedeli: kiracı slug'ı bir uygulama adı olamaz —
 * 'admin' için zaten böyleydi.
 */
const RESERVED_SUBDOMAINS = new Set([
  'www',
  'app',
  'api',
  'static',
  // uygulama kökleri (appRegistry.prodSubdomain + login/marketing)
  'admin',
  'student',
  'teacher',
  'superadmin',
  'super-admin',
  'login',
  'marketing'
]);

export interface ResolveTenantOptions {
  /** platform base domains, e.g. ['zeredu.com', 'zeredu.local'] */
  baseDomains?: string[];
  /**
   * Hostname'den slug ÇIKARILAMADIĞINDA varsayılacak kiracı (uygulamalar
   * `import.meta.env.VITE_DEV_TENANT_ID` geçirir).
   *
   * ⚠ AD TARİHSEL: yalnız "dev" için değil. TEK SABİT KİRACILI kurulumlarda üretimde de gerekir —
   * kiracı-başına alt alan adı olmayan topolojide (`admin.zercode.uk`, `student.zercode.uk`; hepsi
   * AYNI kiracı) hostname'de kiracı etiketi YOKTUR, çıkarım imkânsızdır. .env.production dosyaları
   * da bunu söyler: "Only set it if this pilot runs a single fixed tenant without per-tenant
   * subdomains."
   *
   * Eskiden bu değer YALNIZ localhost'ta kullanılıyordu; canlıda ayarlı olmasına rağmen yok sayılıyor
   * ve slug null kalıyordu (2026-07-31 arızası: ayar ekranları sonsuz iskelet). Artık çıkarımın
   * başarısız olduğu HER durumda devreye girer ve kaynak 'configured' bildirilir.
   *
   * ⚠ ÇOK KİRACILI kurulumda BOŞ BIRAKIN: doluysa çözülemeyen bir hostname sessizce bu kiracıya
   * düşer — yanlış kiracının verisini göstermenin en kısa yolu.
   */
  devFallback?: string;
  hostname?: string;
}

export function resolveTenant(options: ResolveTenantOptions = {}): TenantResolution {
  const hostname = (options.hostname ?? window.location.hostname).toLowerCase();
  // ⚠ ÜRETİM TABAN ALAN ADI BU LİSTEDE OLMAK ZORUNDA. Eskiden yalnız ['zeredu.com','zeredu.local']
  // vardı ve canlıdaki `*.zercode.uk` hiçbiriyle eşleşmiyordu → slug null → tenant.slug'a bağlı
  // sorgular `enabled: false` ile HİÇ koşmuyordu. React Query v5'te devre dışı bir sorgu
  // `status: 'pending'` + `fetchStatus: 'idle'` bildirir, yani isPending TRUE ve isError FALSE:
  // ekran SONSUZA KADAR iskelet gösterir, hata da vermez. Marka/Dil/Açılış ayar ekranları canlıda
  // tam olarak böyle boş kaldı (2026-07-31). apps/login bunu kendi içinde elle listeleyerek
  // aşmıştı ve yorumunda "tracked separately" diyordu — merkezi düzeltme burasıdır.
  const baseDomains = options.baseDomains ?? ['zercode.uk', 'zeredu.com', 'zeredu.local'];

  if (LOCAL_HOSTS.has(hostname) || /^\d+\.\d+\.\d+\.\d+$/.test(hostname)) {
    return options.devFallback
      ? { slug: options.devFallback, hostname, source: 'dev-fallback' }
      : { slug: null, hostname, source: 'none' };
  }

  for (const base of baseDomains) {
    // Apex (ör. zercode.uk): kiracı etiketi yok → yapılandırılmışa düş.
    if (hostname === base) return configured(options.devFallback, hostname, 'none');
    if (hostname.endsWith(`.${base}`)) {
      const label = hostname.slice(0, -(base.length + 1));
      const slug = label.split('.').pop() ?? label; // deepest label closest to base domain
      if (slug && !RESERVED_SUBDOMAINS.has(slug)) {
        return { slug, hostname, source: 'subdomain' };
      }
      // Rezerve/uygulama etiketi (`admin.zercode.uk` → 'admin'): tek-kiracılı topolojinin TAM olarak
      // bu durumu; çıkarım imkânsız → yapılandırılmışa düş.
      return configured(options.devFallback, hostname, 'none');
    }
  }

  // whitelabel custom domain — Control Plane maps hostname -> tenant
  return configured(options.devFallback, hostname, 'custom-domain');
}

/**
 * Çıkarım başarısızsa yapılandırılmış kiracıyı kullanır; yoksa çözümsüzlüğü OLDUĞU GİBİ bildirir.
 * Kaynağı 'configured' işaretlemek önemli: 'subdomain' demek hostname'in kiracıyı gerçekten
 * taşıdığını ima eder ve teşhiste yanlış yere baktırırdı.
 */
function configured(
  fallback: string | undefined,
  hostname: string,
  sourceIfNone: TenantSource
): TenantResolution {
  return fallback
    ? { slug: fallback, hostname, source: 'configured' }
    : { slug: null, hostname, source: sourceIfNone };
}
